import { useCallback, useState } from "react";
import { AgGrid, DropdownButton, Text, toast } from "@jsq/ds/core";
import {
  useCreateAllEntitiesExportMutation,
  useCreateFaEntitiesExportMutation,
  useCreateSoftwareEntitiesExportMutation,
} from "~/graphql/supergraphHooks";
import { useBillingDate } from "~/pages/Billing/contexts/BillingDateContext";
import { getBillingFilterVars } from "~/pages/Billing/utils";

interface EntitiesExportProps {
  gridApi: AgGrid.GridApi | undefined;
}

export const EntitiesExport = ({ gridApi }: EntitiesExportProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const { asOfDateString } = useBillingDate();

  const [createAllEntitiesExport] = useCreateAllEntitiesExportMutation();
  const [createFAEntitiesExport] = useCreateFaEntitiesExportMutation();
  const [createSoftwareEntitiesExport] = useCreateSoftwareEntitiesExportMutation();

  const handleExport = useCallback(
    async (exportType: string) => {
      if (!gridApi || isExporting) return;

      setIsExporting(true);
      try {
        const filters = getBillingFilterVars(gridApi.getFilterModel());

        let mutationResult;
        switch (exportType) {
          case "entities_all": {
            mutationResult = await createAllEntitiesExport({
              variables: {
                exportType: "entities_all",
                asOfDate: asOfDateString,
                filters: filters || [],
              },
            });
            break;
          }
          case "entities_fa": {
            mutationResult = await createFAEntitiesExport({
              variables: {
                exportType: "entities_fa",
                asOfDate: asOfDateString,
                filters: filters || [],
                productLine: ["Fund Administration", "Deal Services", "Fund services"],
              },
            });
            break;
          }
          case "entities_software": {
            mutationResult = await createSoftwareEntitiesExport({
              variables: {
                exportType: "entities_software",
                asOfDate: asOfDateString,
                filters: filters || [],
                productLine: ["Software"],
              },
            });
            break;
          }
          default:
            throw new Error(`Unknown export type: ${exportType}`);
        }

        if (mutationResult?.data?.createEntitiesExport) {
          const result = mutationResult.data.createEntitiesExport;
          const status = result.status;
          const message = result.message;

          if (status === "PENDING") {
            toast.success(
              "A link to download the entities export CSV will be sent to your email shortly.",
            );
          } else {
            toast.error(message || "Export failed");
          }
        }
        if (!mutationResult?.data?.createEntitiesExport) {
          return toast.error("Export failed - please try again.");
        }
      } catch {
        toast.error("Export failed - please try again.");
      } finally {
        setIsExporting(false);
      }
    },
    [
      gridApi,
      isExporting,
      asOfDateString,
      createAllEntitiesExport,
      createFAEntitiesExport,
      createSoftwareEntitiesExport,
    ],
  );

  if (!gridApi) return null;

  return (
    <DropdownButton
      label="Export entities"
      variant="neutral"
      align="right"
      disabled={isExporting}
    >
      <div>
        <Text variant="secondary-sm">EXPORTS WILL RESPECT TABLE FILTERS:</Text>
      </div>
      <DropdownButton.Action onSelect={() => handleExport("entities_all")}>
        All entities
      </DropdownButton.Action>
      <DropdownButton.Action onSelect={() => handleExport("entities_fa")}>
        FA entities
      </DropdownButton.Action>
      <DropdownButton.Action onSelect={() => handleExport("entities_software")}>
        Software entities
      </DropdownButton.Action>
    </DropdownButton>
  );
};
